import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';

/**
 * Balises envoyées par l'API dans la clé « seo » d'une réponse.
 *
 * Toutes optionnelles : le front et le back se déploient séparément, la clé peut
 * donc manquer le temps d'une mise en production.
 */
export interface SeoTags {
  title?: string | null;
  description?: string | null;
  canonical?: string | null;
  image?: string | null;
  jsonld?: string | null;
  /** Absent sur les pages dont l'indexabilité ne dépend pas de leur contenu. */
  indexable?: boolean;
}

/**
 * SeoTagsService
 *
 * Pose dans le <head> tout ce qu'une page doit déclarer aux moteurs : titre,
 * meta description, balises de partage, lien canonique et données structurées
 * JSON-LD. Les deux dernières sont celles que le service Meta d'Angular ne sait
 * pas gérer.
 *
 * Pourquoi c'est utile alors que le site a déjà un rendu serveur pour les IA :
 * ce rendu (règle Apache -> /seo/render/ côté Symfony) ne concerne QUE les
 * robots qui n'exécutent pas JavaScript. Googlebot, lui, exécute JavaScript et
 * reçoit donc l'application Angular. Sans ces balises, il ne voit ni l'URL
 * canonique de la page, ni ce qu'elle décrit.
 *
 * Le contenu n'est jamais construit ici : il vient de la clé « seo » de l'API,
 * calculée par les services App\Service\Seo\{BienSeo, ClubSeo, ConseillerSeo,
 * DepartementSeo} — les mêmes qui alimentent les pages rendues aux robots d'IA.
 */
@Injectable({providedIn: 'root'})
export class SeoTagsService {
  /** Ids portés par les balises que ce service gère, pour les retrouver et les remplacer. */
  private static readonly ID_JSONLD = 'tdm-seo-jsonld';
  private static readonly ID_CANONICAL = 'tdm-seo-canonical';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private title: Title,
    private meta: Meta
  ) {}

  /**
   * Applique d'un coup toutes les balises d'une page.
   *
   * Écrit une seule fois ce que trois composants posaient chacun à leur façon :
   * sans cela, deux pages finissent par déclarer leur description sous des noms
   * d'attribut différents — c'est exactement le défaut que ça corrige (les
   * composants écrivaient « name: og:description », alors qu'Open Graph lit
   * l'attribut « property » ; toutes ces balises étaient donc ignorées par
   * Facebook et LinkedIn).
   *
   * @param seo   Clé « seo » de la réponse d'API, éventuellement absente.
   * @param repli Titre et description à utiliser si le back ne les envoie pas
   *              encore. Le repli est volontaire : les deux dépôts se déploient
   *              séparément.
   */
  apply(seo: SeoTags | null | undefined, repli: {title: string; description?: string | null}): void {
    const titre = seo?.title || repli.title;
    const description = seo?.description || repli.description || '';

    this.title.setTitle(titre);
    this.meta.updateTag({name: 'description', content: description});

    this.meta.updateTag({property: 'og:title', content: titre});
    this.meta.updateTag({property: 'og:description', content: description});
    if (seo?.canonical) {
      // Sans ça, og:url garde la valeur posée dans index.html, soit la page
      // d'accueil : tout partage renverrait à la racine du site.
      this.meta.updateTag({property: 'og:url', content: seo.canonical});
    }
    if (seo?.image) {
      this.meta.updateTag({property: 'og:image', content: seo.image});
    }

    this.setRobots(seo?.indexable);
    this.setCanonical(seo?.canonical);
    this.setJsonLd(seo?.jsonld);
  }

  /**
   * Déclare la page non indexable quand le back le dit.
   *
   * Un seul cas aujourd'hui : un département sans aucun conseiller indépendant.
   * Le sitemap ne l'annonce pas et la page rendue aux robots d'IA le déclare
   * déjà — la version Angular disait le contraire.
   *
   * « follow » est conservé : les liens de la page restent à suivre, c'est par
   * eux qu'un robot atteint les fiches de conseillers.
   *
   * La balise est RETIRÉE quand la page est indexable. Sans ça, un « noindex »
   * posé sur un département survivrait à la navigation vers le suivant — les
   * pages d'une application Angular partagent le même <head>.
   */
  private setRobots(indexable: boolean | undefined): void {
    if (indexable === false) {
      this.meta.updateTag({name: 'robots', content: 'noindex, follow'});
      return;
    }

    this.meta.removeTag("name='robots'");
  }

  /**
   * Remplace le bloc de données structurées de la page.
   *
   * @param json JSON-LD déjà encodé par le back. On ne le reconstruit pas côté
   *             client : la chaîne servie aux robots d'IA et celle injectée ici
   *             restent ainsi identiques au caractère près.
   */
  setJsonLd(json: string | null | undefined): void {
    this.removeById(SeoTagsService.ID_JSONLD);

    if (!json) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = SeoTagsService.ID_JSONLD;
    script.type = 'application/ld+json';
    // textContent et non innerHTML : le contenu est inséré comme du texte, un
    // « </script> » présent dans une description ne peut donc pas refermer la
    // balise et casser la page.
    script.textContent = json;
    this.document.head.appendChild(script);
  }

  /**
   * Déclare l'URL de référence de la page.
   *
   * Indispensable sur ce site : la même fiche est atteignable par plusieurs
   * chemins (la ville et le nombre de pièces font partie de l'URL, au singulier
   * comme au pluriel). Sans canonical, ce sont autant de pages différentes aux
   * yeux d'un moteur, qui se concurrencent entre elles.
   */
  setCanonical(url: string | null | undefined): void {
    if (!url) {
      this.removeById(SeoTagsService.ID_CANONICAL);
      return;
    }

    let link = this.document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      // L'id n'est posé que sur la balise que NOUS créons : clear() ne
      // supprimera donc jamais un canonical venu d'ailleurs.
      link.id = SeoTagsService.ID_CANONICAL;
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  /**
   * À appeler en quittant la page : sans ça, le JSON-LD d'une annonce resterait
   * dans le document pendant la navigation vers la suivante.
   */
  clear(): void {
    this.removeById(SeoTagsService.ID_JSONLD);
    this.removeById(SeoTagsService.ID_CANONICAL);
    this.meta.removeTag("name='robots'");
  }

  private removeById(id: string): void {
    const existant = this.document.getElementById(id);
    if (existant && existant.parentNode) {
      existant.parentNode.removeChild(existant);
    }
  }
}
